import React, { useState, useEffect } from 'react';
import { Client, ClientMetrics as ClientMetricsType, ClientIntelligence as ClientIntelligenceType } from '../types/clientIntelligence';
import { ClientSearch } from '../components/clientIntelligence/ClientSearch';
import { ClientMetrics } from '../components/clientIntelligence/ClientMetrics';
import { IntelligenceProfile } from '../components/clientIntelligence/IntelligenceProfile';
import { NeuralPrepModal } from '../components/clientIntelligence/NeuralPrepModal';
import { NeuralCheckInModal } from '../components/clientIntelligence/NeuralCheckInModal';
import { ConfidenceMeter, EngagementHeatmap, RiskIndicator } from '../components/clientIntelligence/VisualComponents';
import { generateNeuralCheckIn } from '../utils/emailPrepUtils';

const ClientIntelligence: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clientMetrics, setClientMetrics] = useState<ClientMetricsType | null>(null);
  const [intelligence, setIntelligence] = useState<ClientIntelligenceType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Neural Prep functionality
  const [neuralPrep, setNeuralPrep] = useState<any | null>(null);
  const [neuralPrepLoading, setNeuralPrepLoading] = useState(false);
  const [showNeuralPrepModal, setShowNeuralPrepModal] = useState(false);

  // Neural Check-in functionality
  const [checkInEmail, setCheckInEmail] = useState<any | null>(null);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await fetch('/api/clients/list?limit=100', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json() as { success: boolean; clients: Client[] };
        if (data.success) {
          setClients(data.clients);
        }
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchClientMetrics = async (client: Client): Promise<ClientMetricsType | null> => {
    try {
      const response = await fetch(`/api/clients/${client.id}/transcripts?limit=100`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          transcripts: Array<{
            transcript_id: string;
            created_at: string;
            sentiment_score?: number;
            relationship_created_at: string;
          }>;
          pagination?: { total: number };
        };
        
        if (data.success) {
          const transcripts = data.transcripts || [];
          const transcriptCount = data.pagination?.total || transcripts.length;
          
          // Calculate engagement metrics
          const now = new Date();
          const recentTranscripts = transcripts.filter(t => {
            const transcriptDate = new Date(t.created_at);
            const daysDiff = (now.getTime() - transcriptDate.getTime()) / (1000 * 60 * 60 * 24);
            return daysDiff <= 90; // Last 90 days
          });
          
          // Calculate average sentiment
          const sentimentScores = transcripts
            .map(t => t.sentiment_score)
            .filter((score): score is number => score !== undefined && score !== null);
          const avgSentimentScore = sentimentScores.length > 0 
            ? sentimentScores.reduce((a, b) => a + b, 0) / sentimentScores.length 
            : undefined;
          
          // Determine engagement level
          let engagementLevel: 'low' | 'medium' | 'high' = 'low';
          if (recentTranscripts.length >= 5) engagementLevel = 'high';
          else if (recentTranscripts.length >= 2) engagementLevel = 'medium';
          
          // Get last interaction date
          const lastInteractionDate = transcripts.length > 0 
            ? transcripts[0].created_at 
            : undefined;
          
          return {
            transcriptCount,
            lastInteractionDate,
            avgSentimentScore,
            engagementLevel,
            isActive: client.client_status === 'active',
            isPremium: (client.annual_income || 0) > 100000 // $100k+ income threshold
          };
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching client metrics:', error);
      return null;
    }
  };

  const selectClient = async (client: Client) => {
    setSelectedClient(client);
    setIntelligence(null); // Reset previous results
    setClientMetrics(null); // Reset previous metrics
    setError(null);
    
    // Fetch client metrics
    const metrics = await fetchClientMetrics(client);
    setClientMetrics(metrics);
  };

  const clearSelection = () => {
    setSelectedClient(null);
    setIntelligence(null);
    setError(null);
    setClientMetrics(null);
  };

  const generateNeuralCheckInEmail = async () => {
    if (!selectedClient) return;
    
    setCheckInLoading(true);
    setCheckInEmail(null);
    
    try {
      const emailData = await generateNeuralCheckIn(selectedClient.id);
      setCheckInEmail(emailData);
      setShowCheckInModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setCheckInLoading(false);
    }
  };

  const generateNeuralPrep = async () => {
    if (!selectedClient) return;
    
    setNeuralPrepLoading(true);
    setNeuralPrep(null);
    
    try {
      console.log(`🧠 Generating neural prep for client ${selectedClient.id}`);
      
      // First attempt
      let response = await fetch(`/api/clients/${selectedClient.id}/neural-prep-ai`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      // If first attempt fails, wait 2 seconds and try again
      if (!response.ok) {
        console.log('First attempt failed, retrying in 2 seconds...');
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        response = await fetch(`/api/clients/${selectedClient.id}/neural-prep-ai`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
      }

      const data = await response.json() as { 
        success: boolean; 
        neuralPrep?: any; 
        error?: string 
      };
      console.log('Neural Prep API Response:', data);

      if (response.ok && data.success) {
        setNeuralPrep(data.neuralPrep);
        setShowNeuralPrepModal(true);
        console.log('✅ Neural prep generated successfully');
      } else {
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('❌ Neural prep generation failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setNeuralPrepLoading(false);
    }
  };

  const generateIntelligence = async () => {
    if (!selectedClient) return;
    
    setLoading(true);
    setError(null);
    setIntelligence(null);
    
    try {
      console.log(`🧠 Generating intelligence for client ${selectedClient.id}`);
      
      const response = await fetch(`/api/clients/${selectedClient.id}/intelligence`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json() as { 
        success: boolean; 
        intelligence?: ClientIntelligenceType; 
        error?: string 
      };
      console.log('Intelligence API Response:', data);

      if (response.ok && data.success) {
        setIntelligence(data.intelligence!);
        console.log('✅ Intelligence generated successfully');
      } else {
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('❌ Intelligence generation failed:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header - Fixed */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold text-gray-900">Client Intelligence</h1>
              <div className="bg-gradient-to-r from-blue-100 to-purple-100 px-3 py-1 rounded-full">
                <span className="text-sm font-medium text-blue-700">AI-Powered Analysis</span>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Advanced client insights & relationship intelligence
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Client Search */}
          <div className="mb-6">
            <ClientSearch
              clients={clients}
              selectedClient={selectedClient}
              onClientSelect={selectClient}
              onClearSelection={clearSelection}
            />
              </div>
              
          {/* Client Profile Section - Restored Original Design */}
          {selectedClient && (
            <div className="mb-6">
              {/* Client Header with Avatar and Basic Info */}
              <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                  {/* Client Avatar */}
                    <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
                      {selectedClient.first_name?.charAt(0) || ''}
                      {selectedClient.last_name?.charAt(0) || ''}
                  </div>
                  
                  {/* Client Info */}
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                      {selectedClient.first_name} {selectedClient.last_name}
                    </h2>
                      <div className="flex items-center gap-2 text-gray-600 mt-1">
                        <span>📧</span>
                        <span>{selectedClient.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500 text-sm mt-1">
                        <span>📅</span>
                        <span>Client since {selectedClient.created_at ? new Date(selectedClient.created_at).getFullYear() : 'Unknown'}</span>
                    </div>
                  </div>
                </div>
                
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={generateNeuralCheckInEmail}
                      disabled={checkInLoading}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center gap-1 font-medium"
                    >
                      {checkInLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span className="text-sm">Generating...</span>
                        </>
                      ) : (
                        <>
                          <span>📧 Neural Check-in</span>
                          <span className="text-xs opacity-80">Personalized Email</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={generateNeuralPrep}
                      disabled={neuralPrepLoading}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center gap-1 font-medium"
                    >
                      {neuralPrepLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span className="text-sm">Generating...</span>
                        </>
                      ) : (
                        <>
                          <span>🧠 Neural Prep</span>
                          <span className="text-xs opacity-80">AI-powered Intel</span>
                        </>
                      )}
                    </button>
                    
                <button
                  onClick={generateIntelligence}
                  disabled={loading}
                      className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center gap-1 font-medium"
                >
                  {loading ? (
                    <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span className="text-sm">Analyzing...</span>
                          <span className="text-xs opacity-75">Neural processing active</span>
                    </>
                  ) : (
                    <>
                          <span>🧠 Generate Client Neural Intelligence</span>
                          <span className="text-xs opacity-80">Advanced AI analysis in ~30s</span>
                    </>
                  )}
                </button>
                  </div>
              </div>
              
                {/* Client Status Badges */}
                <div className="flex items-center gap-2 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-green-700">Active Portfolio</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-red-500">📄</span>
                    <span className="text-sm text-gray-600">
                      {clientMetrics?.transcriptCount || 0} Transcripts
                    </span>
                </div>
            </div>
              </div>

          {/* Intelligence Results */}
          {intelligence && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* First Column - Relationship Health */}
                  <div className="space-y-6">
              {/* Relationship Health */}
              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg border border-green-200 p-6 h-auto max-h-[500px] overflow-y-auto">
                <h3 className="font-semibold text-green-800 mb-4 flex items-center gap-2 sticky top-0 bg-gradient-to-br from-green-50 to-blue-50 pb-2 border-b border-green-200">
                  💚 Relationship Health & Trends
                </h3>
                
                      <ConfidenceMeter confidence={intelligence.metrics.aiConfidence} gradient={{ from: '#4ade80', to: '#16a34a' }} />
                
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4 border border-green-100 hover:shadow-md transition-shadow">
                          <h4 className="font-medium text-sm text-green-700 mb-3 flex items-center justify-between">
                      📈 Sentiment Journey
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                              {intelligence.metrics.sentimentProgression.length > 1 && 
                               intelligence.metrics.sentimentProgression[intelligence.metrics.sentimentProgression.length - 1] > 
                               intelligence.metrics.sentimentProgression[0] ? 'Trending Up' : 'Stable'}
                            </span>
                    </h4>
                    <div className="text-sm text-green-600 mb-3">
                      {intelligence.relationshipHealth.sentimentJourney}
                    </div>
                    
                          {/* Real Sentiment Timeline Visual */}
                    <div className="flex items-center gap-2 mt-3">
                      <span className="text-xs text-gray-500">Past</span>
                      <div className="flex-1 flex items-center gap-1">
                              {intelligence.metrics.sentimentProgression.map((sentiment, i) => (
                          <div key={i} className="flex-1 space-y-1">
                            <div className={`h-8 rounded-sm ${sentiment >= 80 ? 'bg-green-500' : sentiment >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`} 
                                 style={{ height: `${sentiment * 0.4}px` }} />
                            <div className="text-xs text-center text-gray-500">{sentiment}%</div>
                          </div>
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">Recent</span>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-green-100 hover:shadow-md transition-shadow">
                    <h4 className="font-medium text-sm text-green-700 mb-3 flex items-center justify-between">
                      🎯 Relationship Score
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                              <span className="text-xs text-green-600 capitalize">{intelligence.metrics.trustLevel}</span>
                      </div>
                    </h4>
                    
                    {/* Enhanced Progress Ring */}
                    <div className="flex items-center gap-4 mb-3">
                      <div className="relative w-16 h-16">
                        <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="rgb(229, 231, 235)"
                            strokeWidth="2"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="rgb(34, 197, 94)"
                            strokeWidth="2"
                            strokeDasharray={`${intelligence.relationshipHealth.score}, 100`}
                            className="animate-pulse"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-lg font-bold text-green-700">
                            {intelligence.relationshipHealth.score}%
                          </span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-green-600 mb-2">
                          {intelligence.relationshipHealth.scoreDescription}
                        </p>
                        <div className="flex gap-2">
                                <span className={`text-xs px-2 py-1 rounded ${
                                  intelligence.metrics.trustLevel === 'high' ? 'bg-green-100 text-green-700' :
                                  intelligence.metrics.trustLevel === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>Trust Level: {intelligence.metrics.trustLevel}</span>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  intelligence.metrics.loyaltyLevel === 'strong' ? 'bg-blue-100 text-blue-700' :
                                  intelligence.metrics.loyaltyLevel === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>Loyalty: {intelligence.metrics.loyaltyLevel}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-green-100 hover:shadow-md transition-shadow">
                    <h4 className="font-medium text-sm text-green-700 mb-3">🔄 Engagement Patterns</h4>
                    <div className="text-sm text-green-600 mb-4">
                      {intelligence.relationshipHealth.engagementPatterns}
                    </div>
                    
                          {/* Real Engagement Heatmap */}
                          <div className="space-y-2">
                            <h5 className="text-xs font-medium text-gray-600">
                              Engagement History ({intelligence.metrics.engagementHistory.length} {intelligence.metrics.engagementHistory.length === 1 ? 'week' : 'weeks'})
                            </h5>
                            <div className="flex gap-1">
                              {intelligence.metrics.engagementHistory.map((week) => {
                                const getHeatColor = (value: number) => {
                                  if (value >= 80) return 'bg-green-500';
                                  if (value >= 60) return 'bg-yellow-500';
                                  if (value >= 40) return 'bg-orange-500';
                                  return 'bg-red-500';
                                };
                                return (
                                  <div
                                    key={week.week}
                                    className={`w-4 h-4 rounded-sm ${getHeatColor(week.score)} opacity-80 hover:opacity-100 transition-opacity`}
                                    title={`Week ${week.week}: ${week.score}% engagement`}
                                  />
                                );
                              })}
                            </div>
                            {intelligence.metrics.engagementHistory.length === 1 && (
                              <div className="text-xs text-gray-500 mt-1">
                                📅 Limited data: All transcripts added within the same week
                              </div>
                            )}
                          </div>
                          
                          {/* Real Engagement Metrics */}
                    <div className="grid grid-cols-3 gap-2 mt-4">
                      <div className="text-center p-2 bg-green-50 rounded">
                              <div className="text-lg font-bold text-green-700">{intelligence.metrics.engagementMetrics.avgResponseTimeHours}</div>
                        <div className="text-xs text-green-600">Avg Response Time (hrs)</div>
                      </div>
                      <div className="text-center p-2 bg-blue-50 rounded">
                              <div className="text-lg font-bold text-blue-700">{intelligence.metrics.engagementMetrics.responseRate}%</div>
                              <div className="text-xs text-blue-600">Engagement Consistency</div>
                      </div>
                      <div className="text-center p-2 bg-purple-50 rounded">
                              <div className="text-lg font-bold text-purple-700">{intelligence.metrics.engagementMetrics.interactionsPerMonth}</div>
                              <div className="text-xs text-purple-600">This Month</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
                    </div>
                    
                  {/* Second Column - Profile */}
                  <div className="space-y-6">
                    <IntelligenceProfile intelligence={intelligence} />
                  </div>

                  {/* Third Column - Business Opportunities */}
                  <div className="space-y-6">
              {/* Business Opportunities */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg border border-orange-200 p-6 h-auto max-h-[500px] overflow-y-auto">
                <h3 className="font-semibold text-orange-800 mb-4 flex items-center gap-2 sticky top-0 bg-gradient-to-br from-orange-50 to-red-50 pb-2 border-b border-orange-200">
                  🚀 Business Opportunities & Actions
                </h3>
                
                      <ConfidenceMeter confidence={intelligence.opportunities.metrics?.businessMetrics?.engagementOpportunity || 75} gradient={{ from: '#fb923c', to: '#ef4444' }} />
                
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4 border border-orange-100 hover:shadow-md transition-shadow">
                    <h4 className="font-medium text-sm text-orange-700 mb-3 flex items-center gap-2">
                            🌟 Emerging Opportunities
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">{intelligence.opportunities.metrics.opportunityAnalysis.length} Identified</span>
                    </h4>
                    <div className="text-sm text-orange-600 mb-4">
                      {intelligence.opportunities.emerging}
                    </div>
                    
                    {/* Opportunity Value Matrix */}
                    <div className="grid grid-cols-1 gap-3">
                            {intelligence.opportunities.metrics.opportunityAnalysis.slice(0, 3).map((opportunity, index) => {
                              const colorSchemes = [
                                {
                                  bg: 'bg-gradient-to-r from-green-50 to-green-100',
                                  border: 'border-green-500',
                                  text: 'text-green-800',
                                  subtext: 'text-green-600',
                                  valueText: 'text-green-700'
                                },
                                {
                                  bg: 'bg-gradient-to-r from-blue-50 to-blue-100', 
                                  border: 'border-blue-500',
                                  text: 'text-blue-800',
                                  subtext: 'text-blue-600',
                                  valueText: 'text-blue-700'
                                },
                                {
                                  bg: 'bg-gradient-to-r from-purple-50 to-purple-100',
                                  border: 'border-purple-500', 
                                  text: 'text-purple-800',
                                  subtext: 'text-purple-600',
                                  valueText: 'text-purple-700'
                                }
                              ];
                              
                              const scheme = colorSchemes[index] || colorSchemes[0];
                              const potentialValues = {
                                'high': '$2.1M',
                                'medium': '$350K', 
                                'low': '$120K'
                              };
                              const potentialLabels = {
                                'high': 'High-Value Opportunity',
                                'medium': 'Medium-Value Opportunity',
                                'low': 'Emerging Opportunity'
                              };
                              
                              return (
                                <div key={opportunity.category} className={`flex items-center justify-between p-3 ${scheme.bg} rounded-lg border-l-4 ${scheme.border}`}>
                        <div>
                                    <div className={`font-medium ${scheme.text} text-sm`}>{opportunity.category}</div>
                                    <div className={`text-xs ${scheme.subtext}`}>{potentialLabels[opportunity.potential]} (mentioned {opportunity.frequency}x)</div>
                        </div>
                        <div className="text-right">
                                    <div className={`text-lg font-bold ${scheme.valueText}`}>{potentialValues[opportunity.potential]}</div>
                                    <div className={`text-xs ${scheme.subtext}`}>Potential Value</div>
                        </div>
                      </div>
                              );
                            })}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-orange-100 hover:shadow-md transition-shadow">
                    <h4 className="font-medium text-sm text-orange-700 mb-3 flex items-center gap-2">
                      ⚠️ Risk Factors
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">Monitor</span>
                    </h4>
                    <div className="text-sm text-orange-600 mb-4">
                      {intelligence.opportunities.risks}
                    </div>
                    
                    {/* Risk Indicators */}
                    <div className="space-y-2">
                            {intelligence.opportunities.metrics.riskFactors.filter(risk => risk.detected).map((risk) => (
                              <RiskIndicator key={risk.risk} level={risk.level} label={risk.risk} />
                            ))}
                            {intelligence.opportunities.metrics.riskFactors.filter(risk => risk.detected).length === 0 && (
                              <div className="flex items-center gap-2 p-2 rounded-md bg-green-50">
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                <span className="text-sm font-medium text-green-700">No immediate risk factors detected</span>
                              </div>
                            )}
                    </div>
                    
                    {/* Risk Dashboard */}
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-600">Overall Risk Level</span>
                              <span className={`text-xs font-bold ${
                                intelligence.opportunities.metrics.riskFactors.filter(r => r.detected && r.level === 'high').length > 0 ? 'text-red-700' :
                                intelligence.opportunities.metrics.riskFactors.filter(r => r.detected && r.level === 'medium').length > 0 ? 'text-yellow-700' :
                                'text-green-700'
                              }`}>
                                {intelligence.opportunities.metrics.riskFactors.filter(r => r.detected && r.level === 'high').length > 0 ? 'HIGH' :
                                 intelligence.opportunities.metrics.riskFactors.filter(r => r.detected && r.level === 'medium').length > 0 ? 'MODERATE' :
                                 'LOW'}
                              </span>
                      </div>
                      <div className="flex gap-1">
                              <div className={`flex-1 h-2 rounded-l ${
                                intelligence.opportunities.metrics.riskFactors.filter(r => r.detected).length === 0 ? 'bg-green-400' : 'bg-gray-300'
                              }`}></div>
                              <div className={`flex-1 h-2 ${
                                intelligence.opportunities.metrics.riskFactors.filter(r => r.detected && r.level === 'medium').length > 0 ? 'bg-yellow-400' : 'bg-gray-300'
                              }`}></div>
                              <div className={`flex-1 h-2 ${
                                intelligence.opportunities.metrics.riskFactors.filter(r => r.detected && r.level === 'high').length > 0 ? 'bg-orange-400' : 'bg-gray-300'
                              }`}></div>
                              <div className={`flex-1 h-2 rounded-r ${
                                intelligence.opportunities.metrics.riskFactors.filter(r => r.detected && r.level === 'high').length >= 2 ? 'bg-red-400' : 'bg-gray-200'
                              }`}></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-orange-100 hover:shadow-md transition-shadow">
                    <h4 className="font-medium text-sm text-orange-700 mb-3 flex items-center gap-2">
                      ✅ Action Items
                            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">{intelligence.opportunities.metrics.actionMetrics.totalActions} Total</span>
                    </h4>
                    <div className="text-sm text-orange-600 mb-4">
                      {intelligence.opportunities.actionItems}
                    </div>
                    
                    {/* Action Priority Board */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 p-2 bg-red-50 rounded-lg border-l-4 border-red-500">
                        <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">!</div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-red-800">Estate Plan Health Check</div>
                          <div className="text-xs text-red-600">Due: This week</div>
                        </div>
                        <button className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors">
                          Schedule
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-3 p-2 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                        <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center text-white text-xs font-bold">2</div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-yellow-800">College Funding Review</div>
                          <div className="text-xs text-yellow-600">Due: Next month</div>
                        </div>
                        <button className="px-3 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600 transition-colors">
                          Plan
                        </button>
                      </div>
                      
                      <div className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">3</div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-blue-800">Investment Reallocation</div>
                          <div className="text-xs text-blue-600">Due: Q2 2025</div>
                        </div>
                        <button className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors">
                          Review
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-orange-100 hover:shadow-md transition-shadow">
                    <h4 className="font-medium text-sm text-orange-700 mb-3 flex items-center gap-2">
                      🎯 Strategic Recommendations
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">Roadmap</span>
                    </h4>
                    <div className="text-sm text-orange-600 mb-4">
                      {intelligence.opportunities.recommendations}
                    </div>
                    
                    {/* Strategic Timeline */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="text-center min-w-16">
                          <div className="text-xs text-gray-500">Q1 2025</div>
                          <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mt-1"></div>
                        </div>
                        <div className="flex-1 bg-green-50 p-2 rounded text-sm">
                          <div className="font-medium text-green-800">Execute Estate Planning Updates</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="text-center min-w-16">
                          <div className="text-xs text-gray-500">Q2 2025</div>
                          <div className="w-3 h-3 bg-blue-500 rounded-full mx-auto mt-1"></div>
                        </div>
                        <div className="flex-1 bg-blue-50 p-2 rounded text-sm">
                          <div className="font-medium text-blue-800">Implement Tax Optimization</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="text-center min-w-16">
                          <div className="text-xs text-gray-500">Q3 2025</div>
                          <div className="w-3 h-3 bg-purple-500 rounded-full mx-auto mt-1"></div>
                        </div>
                        <div className="flex-1 bg-purple-50 p-2 rounded text-sm">
                          <div className="font-medium text-purple-800">Practice Expansion Phase 1</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-4">
                      <button className="flex-1 px-3 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white text-sm rounded-lg hover:from-orange-600 hover:to-red-600 transition-colors">
                        📊 Export Report
                      </button>
                      <button className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm rounded-lg hover:from-blue-600 hover:to-purple-600 transition-colors">
                        📅 Schedule Review
                      </button>
                          </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-red-600">❌</span>
                <span className="text-red-800 font-medium">Error:</span>
                <span className="text-red-700">{error}</span>
      </div>
            </div>
          )}

          {/* No Client Selected State */}
          {!selectedClient && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🧠</div>
              <h2 className="text-2xl font-bold text-gray-700 mb-2">Client Intelligence Dashboard</h2>
              <p className="text-gray-500 mb-6">Search and select a client to generate AI-powered insights</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
                <h3 className="font-semibold text-blue-800 mb-3">What you'll get:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
                  <div className="flex items-center gap-2">
                    <span>🧠</span>
                    <span>Neural Prep for meetings</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>💬</span>
                    <span>Communication style analysis</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>🎯</span>
                    <span>Key priorities & decision making</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>💚</span>
                    <span>Relationship health insights</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>🚀</span>
                    <span>Business opportunities</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>📊</span>
                    <span>Engagement metrics</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Neural Check-in Modal */}
      <NeuralCheckInModal
        isOpen={showCheckInModal}
        onClose={() => setShowCheckInModal(false)}
        checkInEmail={checkInEmail}
        clientName={selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : ''}
        loading={checkInLoading}
      />

      {/* Neural Prep Modal */}
      <NeuralPrepModal
        isOpen={showNeuralPrepModal}
        onClose={() => setShowNeuralPrepModal(false)}
        neuralPrep={neuralPrep}
        clientName={selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : ''}
        loading={neuralPrepLoading}
      />
    </div>
  );
};

export default ClientIntelligence; 